<template>
    <div class="px-8">
        <div class="flex justify-between items-center">
            <div class="font-semibold text-lg">
                Cottonseed Bids
            </div>
            <div>
                <div>
                    <nav class="flex space-x-4" aria-label="Tabs">
                        <!-- Current: "bg-indigo-100 text-indigo-700", Default: "text-gray-500 hover:text-gray-700" -->
                        <a @click.prevent="showOpen = 1" href="#" :class="[ showOpen ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700', 'px-3 py-2 font-medium text-sm rounded-md']">
                            Open
                        </a>

                        <a @click.prevent="showOpen = 0" href="#" :class="[ ! showOpen ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700', 'px-3 py-2 font-medium text-sm rounded-md']">
                            Closed
                        </a>
                    </nav>
                </div>
            </div>
            <add-cottonseed-bid-item :gins="gins" :zones="zones"></add-cottonseed-bid-item>
        </div>

        <div class="flex flex-col mt-4">
            <div class="-my-2 sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th @click="sortBy('quantity')" :class="{'font-extrabold' : sortKey === 'quantity' }" scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <div class="cursor-pointer flex items-center justify-between">
                                            <span>Qty</span>
                                            <span v-if="sortKey === 'quantity'">
                                                <svg :class="{'transform rotate-180' : sortOrder === 'desc' }" class="w-4 h-4 ml-1 transition duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"></path></svg>
                                            </span>
                                        </div>
                                    </th>
                                    <th @click="sortBy('price')" :class="{'font-extrabold' : sortKey === 'price' }" scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <div class="cursor-pointer flex items-center justify-between">
                                            <span>Price</span>
                                            <span v-if="sortKey === 'price'">
                                                <svg :class="{'transform rotate-180' : sortOrder === 'desc' }" class="w-4 h-4 ml-1 transition duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"></path></svg>
                                            </span>
                                        </div>
                                    </th>
                                    <th @click="sortBy('transport')" :class="{'font-extrabold' : sortKey === 'transport' }" scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <div class="cursor-pointer flex items-center justify-between">
                                            <span>Transport</span>
                                            <span v-if="sortKey === 'transport'">
                                                <svg :class="{'transform rotate-180' : sortOrder === 'desc' }" class="w-4 h-4 ml-1 transition duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"></path></svg>
                                            </span>
                                        </div>
                                    </th>
                                    <th @click="sortBy('location')" :class="{'font-extrabold' : sortKey === 'location' }" scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <div class="cursor-pointer flex items-center justify-between">
                                            <span>Location</span>
                                            <span v-if="sortKey === 'location'">
                                                <svg :class="{'transform rotate-180' : sortOrder === 'desc' }" class="w-4 h-4 ml-1 transition duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"></path></svg>
                                            </span>
                                        </div>
                                    </th>
                                    <th @click="sortBy('expires_at')" :class="{'font-extrabold' : sortKey === 'expires_at' }" scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <div class="cursor-pointer flex items-center justify-between">
                                            <span>Expires</span>
                                            <span v-if="sortKey === 'expires_at'">
                                                <svg :class="{'transform rotate-180' : sortOrder === 'desc' }" class="w-4 h-4 ml-1 transition duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"></path></svg>
                                            </span>
                                        </div>
                                    </th>
                                    <th scope="col" class="relative px-6 py-3">
                                        <span class="sr-only">Edit</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, idx) in sortedCottonseedBid" :class="{ 'bg-white' : idx % 2 === 0, 'bg-gray-50' : idx % 2 !== 0, 'bg-blue-50' : showMenu === item.id }">
                                    <td class="px-6 py-4 text-gray-800 ">
                                        <p class="text-sm overflow-ellipsis overflow-hidden">{{ item.quantity }}</p>
                                    </td>
                                    <td class="px-6 py-4 text-gray-800 ">
                                        <p class="text-sm overflow-ellipsis overflow-hidden">{{ item.price | currency }}</p>
                                    </td>
                                    <td class="px-6 py-4 text-gray-800 ">
                                        <p class="text-sm overflow-ellipsis overflow-hidden">{{ item.transport }}</p>
                                    </td>
                                    <td class="px-6 py-4 text-gray-800 ">
                                        <p class="text-sm overflow-ellipsis overflow-hidden">{{ item.location }}</p>
                                    </td>

                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 align-top">
                                        {{ item.expires_at | niceDate }}
                                    </td>
                                    <td class="px-6 py-4 align-top text-right text-sm font-medium">

                                        <div class="relative inline-block text-left">
                                            <div>
                                                <button @click="setShowMenu(item.id)" class="w-8 h-8 inline-flex items-center justify-center text-gray-400 rounded-full hover:text-gray-500 focus:outline-none" id="options-menu" aria-haspopup="true" aria-expanded="true">
                                                    <span class="sr-only">Open options</span>
                                                    <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                                    </svg>
                                                </button>
                                            </div>
                                            <div v-if="item.id === showMenu" class="origin-top-right absolute z-10 right-0 top-0 mr-8 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                                                <div class="py-1" role="none">
                                                    <edit-cottonseed-bid-item @close-menu="showMenu = null" :item="item"  :gins="gins" :zones="zones"></edit-cottonseed-bid-item>
                                                </div>
                                                <div class="py-1 border-t border-gray-100" role="none">
                                                    <remove-cottonseed-bid-item @close-menu="showMenu = null" :item="item"></remove-cottonseed-bid-item>
                                                </div>
                                            </div>
                                        </div>

                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>

import AddCottonseedBidItem from "./AddCottonseedBidItem";
import EditCottonseedBidItem from "./EditCottonseedBidItem";
import RemoveCottonseedBidItem from "./RemoveCottonseedBidItem";

export default {
    components: {RemoveCottonseedBidItem, EditCottonseedBidItem, AddCottonseedBidItem},
    props: {
        items: {
            type: Array,
            required: true,
        },
        gins: {
            type: Array,
            required: true,
        },
        zones: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            showMenu: null,
            sortKey: 'updated_at',
            sortOrder: 'desc',
            showOpen: 1,
        }
    },
    methods: {
        setShowMenu(id) {
            this.showMenu = this.showMenu === id ? null : id
        },
        sortBy(key) {
            this.sortKey = key
            this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
        },
        compareValues(key, order = 'asc') {
            return function innerSort(a, b) {
                if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) return 0;
                const comparison = a[key].localeCompare(b[key]);

                return (
                    (order === 'desc') ? (comparison * -1) : comparison
                );
            };
        }
    },
    computed: {
        sortedCottonseedBid() {
            return this.items.filter(item => item.is_open === this.showOpen).sort(
                this.compareValues(this.sortKey, this.sortOrder)
            )
        }
    }
}
</script>
